import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    FiHome,
    FiMail,
    FiCheckCircle,
    FiAlertCircle,
    FiClock,
    FiSend
} from "react-icons/fi";
import API_BASE_URL from "../config";
import "../styles/UserVerification.css";

export default function ForgotPassword() {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [step, setStep] = useState(1); // 1 = email, 2 = otp
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [logoUrl, setLogoUrl] = useState(null);
    const [otpTimer, setOtpTimer] = useState(300); // 5 minutes
    const [canResend, setCanResend] = useState(false);

    const otpRefs = useRef(Array(6).fill(null));

    /* ==============================
       LOAD LOGO FROM SITE SETTINGS
    ============================== */
    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/site-settings`);
                const data = await res.json();

                if (data?.logo && data?.logoContentType) {
                    const base64 = `data:${data.logoContentType};base64,${data.logo}`;
                    setLogoUrl(base64);
                }
            } catch (err) {
                console.error("Failed to load logo:", err);
            }
        };

        fetchLogo();
    }, []);

    /* ==============================
       OTP TIMER
    ============================== */
    useEffect(() => {
        if (otpTimer <= 0) {
            setCanResend(true);
            return;
        }

        const interval = setInterval(() => {
            setOtpTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [otpTimer]);

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    };

    /* ==============================
       OTP INPUT HANDLING
    ============================== */
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }

        if (!value && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const getOtpString = () => {
        return otp.join('');
    };

    /* ==============================
       SEND OTP
    ============================== */
    const sendOtp = async () => {
        if (!email) {
            setMessage("Please enter your email address");
            setMessageType("error");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setMessage("Please enter a valid email address");
            setMessageType("error");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password/send-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email })
            });

            const data = await res.json();

            if (!res.ok) {
                setMessage(data.message || "Failed to send OTP");
                setMessageType("error");
                toast.error(data.message);
            } else {
                setMessage("OTP sent to your email address");
                setMessageType("success");
                toast.success("OTP sent to your email");
                setStep(2);
                setOtpTimer(300);
                setCanResend(false);
            }
        } catch (err) {
            console.error(err);
            setMessage("Something went wrong. Please try again.");
            setMessageType("error");
            toast.error("Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    /* ==============================
       VERIFY OTP
    ============================== */
    const verifyOtp = async () => {
        const otpString = getOtpString();

        if (otpString.length !== 6) {
            setMessage("Please enter the complete 6-digit OTP");
            setMessageType("error");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    otp: otpString
                })
            });

            const data = await res.json();

            if (!res.ok) {
                setMessage(data.message || "Invalid OTP");
                setMessageType("error");
                toast.error(data.message);

                // Clear OTP on error
                setOtp(["", "", "", "", "", ""]);
                otpRefs.current[0]?.focus();
            } else {
                setMessage("OTP verified successfully! Redirecting...");
                setMessageType("success");
                toast.success("OTP verified successfully");

                // Redirect to reset password page
                setTimeout(() => {
                    navigate("/reset-password", { state: { email } });
                }, 1500);
            }
        } catch (err) {
            console.error(err);
            setMessage("Something went wrong. Please try again.");
            setMessageType("error");
            toast.error("OTP verification failed");
        } finally {
            setLoading(false);
        }
    };

    /* ==============================
   RESEND OTP  🔁 (FORGOT PASSWORD)
============================== */
    const resendOtp = async () => {
        if (!canResend || !email) return;

        setLoading(true);
        setMessage("");

        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password/send-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email })
            });

            const data = await res.json();

            if (!res.ok) {
                setMessage(data.message || "Failed to resend OTP");
                setMessageType("error");
                toast.error(data.message || "Failed to resend OTP");
            } else {
                setMessage("OTP resent successfully! Check your email.");
                setMessageType("success");
                toast.success("OTP resent successfully 📩");

                // 🔥 Reset timer & state
                setOtpTimer(300);          // 5 minutes again
                setCanResend(false);
                setOtp(["", "", "", "", "", ""]);
                otpRefs.current[0]?.focus();
            }
        } catch (err) {
            console.error(err);
            setMessage("Failed to resend OTP. Please try again.");
            setMessageType("error");
            toast.error("Failed to resend OTP");
        } finally {
            setLoading(false);
        }
    };


    /* ==============================
       HANDLE KEY PRESS
    ============================== */
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (step === 1) {
                sendOtp();
            } else {
                verifyOtp();
            }
        }
    };

    return (
        <div className="premium-verification-page">

            {/* ==============================
         TOP RIGHT HOME BUTTON
      ============================== */}
            <div className="premium-verification-home-container">
                <Link to="/" className="premium-verification-home-btn">
                    <FiHome size={20} />
                    <span>Home</span>
                </Link>
            </div>

            {/* ==============================
         MAIN CONTENT
      ============================== */}
            <div className="premium-verification-container">
                <div className="premium-verification-form-container">
                    <div className="premium-verification-form-card">

                        {/* Logo */}
                        <div className="premium-verification-form-logo">
                            {logoUrl ? (
                                <img src={logoUrl} alt="Brand Logo" className="premium-verification-form-brand-logo" />
                            ) : (
                                <div className="premium-verification-form-brand-text">Justivon</div>
                            )}
                        </div>

                        {/* STEP 1: Enter Email */}
                        {step === 1 && (
                            <>
                                <div className="premium-verification-form-header">
                                    <h2 className="premium-verification-form-title">Forgot Password</h2>
                                    <p className="premium-verification-form-subtitle">
                                        Enter your email address to receive a password reset OTP
                                    </p>
                                </div>

                                {/* Message Display */}
                                {message && (
                                    <div className={`premium-verification-message ${messageType}`}>
                                        {messageType === "error" ? <FiAlertCircle /> : <FiCheckCircle />}
                                        <span>{message}</span>
                                    </div>
                                )}

                                {/* Email Input */}
                                <div className="premium-verification-form-group">
                                    <label className="premium-verification-label">
                                        <FiMail size={16} />
                                        <span>Email Address</span>
                                    </label>
                                    <div className="premium-verification-input-group">
                                        <input
                                            className="premium-verification-input"
                                            type="email"
                                            placeholder="Enter your registered email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                    </div>
                                </div>

                                {/* Send OTP Button */}
                                <button
                                    className="premium-verification-submit-btn"
                                    onClick={sendOtp}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <div className="premium-verification-spinner"></div>
                                    ) : (
                                        <FiSend size={18} />
                                    )}
                                    <span>{loading ? "Sending OTP..." : "Send Reset OTP"}</span>
                                </button>

                                {/* Back to Login */}
                                <div className="back-to-login">
                                    <span>Remember your password?</span>
                                    <Link to="/login" className="back-to-login-link">
                                        Back to Login
                                    </Link>
                                </div>
                            </>
                        )}

                        {/* STEP 2: Verify OTP */}
                        {step === 2 && (
                            <>
                                <div className="premium-verification-form-header">
                                    <h2 className="premium-verification-form-title">Verify OTP</h2>
                                    <p className="premium-verification-form-subtitle">
                                        Enter the 6-digit OTP sent to your email address
                                    </p>
                                </div>

                                {/* Email Info */}
                                <div className="email-info">
                                    <p>OTP sent to:</p>
                                    <div className="email-address">{email}</div>
                                </div>

                                {/* OTP Timer */}
                                <div className="otp-timer">
                                    <FiClock size={16} style={{ marginRight: '8px', marginBottom: "5px", verticalAlign: 'middle' }} />
                                    OTP expires in <strong>{formatTime(otpTimer)}</strong>
                                </div>

                                {/* Message Display */}
                                {message && (
                                    <div className={`premium-verification-message ${messageType}`}>
                                        {messageType === "error" ? <FiAlertCircle /> : <FiCheckCircle />}
                                        <span>{message}</span>
                                    </div>
                                )}

                                {/* OTP Input Boxes */}
                                <div className="otp-container">
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                        <input
                                            key={index}
                                            ref={(el) => (otpRefs.current[index] = el)}
                                            className={`otp-input ${otp[index] ? 'filled' : ''}`}
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength="1"
                                            value={otp[index]}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            onKeyPress={handleKeyPress}
                                            autoFocus={index === 0}
                                        />
                                    ))}
                                </div>

                                {/* Verify Button */}
                                <button
                                    className="premium-verification-submit-btn"
                                    onClick={verifyOtp}
                                    disabled={loading || getOtpString().length !== 6}
                                >
                                    {loading ? (
                                        <div className="premium-verification-spinner"></div>
                                    ) : (
                                        <FiCheckCircle size={18} />
                                    )}
                                    <span>{loading ? "Verifying..." : "Verify OTP"}</span>
                                </button>

                                {/* Resend OTP Button */}
                                <button
                                    className="premium-verification-secondary-btn"
                                    onClick={resendOtp}
                                    disabled={!canResend || loading}
                                >
                                    <FiMail size={18} />
                                    <span>{canResend ? "Resend OTP" : "Resend OTP"}</span>
                                </button>

                                {/* Back to Email Step */}
                                <div className="back-to-login">
                                    <span>Wrong email?</span>
                                    <button
                                        onClick={() => {
                                            setStep(1);
                                            setMessage("");
                                            setOtp(["", "", "", "", "", ""]);
                                        }}
                                        className="back-to-login-link"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            font: 'inherit',
                                            padding: 0
                                        }}
                                    >
                                        Change Email
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}